import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

export interface PlanDetailsResponse {
  insuranceId?: number;
  insuranceName?: string;
  planActive?: boolean;
  planAddress?: string;
  planAddressContact?: string;
  planAnesthesiaBillMinutes?: boolean;
  planAnesthesiaLink?: number;
  planBcbs?: boolean;
  planCapAutoWriteOff?: boolean;
  planCapitated?: boolean;
  planCategoryId?: number;
  planCategoryName?: string;
  planChampVa?: boolean;
  planChampus?: boolean;
  planCity?: string;
  planComments?: string;
  planContact?: string;
  planDescription?: string;
  planElectEOBWriteOff?: boolean;
  planElectSubmit?: boolean;
  planExt?: string;
  planFax?: string;
  planFeca?: boolean;
  planFidelis?: boolean;
  planFormId?: number;
  planGhiMedicare?: boolean;
  planGroup?: boolean;
  planHealthFirst?: boolean;
  planId?: number;
  planManagedCare?: boolean;
  planMedicaid?: boolean;
  planMedicare?: boolean;
  planName?: string;
  planOther?: boolean;
  planOutstandingDays?: number;
  planPINRelation?: boolean;
  planReservedField24k?: boolean;
  planSecondaryBillAllowed?: boolean;
  planState?: string;
  planStatementComments?: string;
  planSubmitVendorId?: number;
  planSubmitVendorName?: string;
  planTel?: string;
  planTelExt?: string;
  planTypeId?: number;
  planZipCode?: string;
}

export interface PlanDropdownsResponse {
  anesthesia?: { id: number; label: string }[];
  category?: { id: number; label: string }[];
  form?: { id: number; label: string }[];
  insurance?: { id: number; label: string }[];
  planType?: { id: number; label: string }[];
  submitVendor?: { id: number; label: string }[];
}

export interface PlanUpdateRequest {
  insuranceId?: number;
  planActive?: boolean;
  planAddress?: string;
  planAddressContact?: string;
  planAnesthesiaBillMinutes?: boolean;
  planAnesthesiaLink?: number;
  planBcbs?: boolean;
  planCapAutoWriteOff?: boolean;
  planCapitated?: boolean;
  planCategoryId?: number;
  planChampVa?: boolean;
  planChampus?: boolean;
  planCity?: string;
  planComments?: string;
  planContact?: string;
  planDescription?: string;
  planElectEobWriteOff?: boolean;
  planElectSubmit?: boolean;
  planExt?: string;
  planFax?: string;
  planFeca?: boolean;
  planFidelis?: boolean;
  planFormId?: number;
  planGhiMedicare?: boolean;
  planGroup?: boolean;
  planHealthFirst?: boolean;
  planManagedCare?: boolean;
  planMedicaid?: boolean;
  planMedicare?: boolean;
  planName?: string;
  planOther?: boolean;
  planOutstandingDays?: number;
  planPinRelation?: boolean;
  planReservedField24k?: boolean;
  planSecondaryBillAllowed?: boolean;
  planState?: string;
  planStatementComments?: string;
  planSubmitVendorId?: number;
  planTel?: string;
  planTelExt?: string;
  planTypeId?: number;
  planZipCode?: string;
}

export interface PlanUpdateResponse {
  message?: string;
  planId?: number;
  success?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PlanService {
  constructor(private apiService: ApiService) {}

  /**
   * GET /plans/{planId}
   * Fetch administrator plan details for Add/Edit Plan screen.
   * API returns { status, message, data: { planId, planName, ... } }; we unwrap data so callers get the plan object.
   */
  getPlanDetails(planId: number | string): Observable<PlanDetailsResponse> {
    return this.apiService.get<{ status?: string; message?: string; data?: PlanDetailsResponse }>(`plans/${planId}`).pipe(
      map((res: any) => (res?.data != null ? res.data : res) as PlanDetailsResponse),
      catchError((err: HttpErrorResponse) => {
        const status = err?.status;
        const message =
          err?.error?.message || err?.message || err?.statusText || 'Failed to load plan details';
        return throwError(() => ({ status, message }));
      })
    );
  }

  /**
   * GET /plans/dropdowns
   * Optional query: ?dropdown=insurance,category,submitVendor,form,anesthesia,planType
   * Returns normalized dropdowns object (payload.data or payload).
   */
  getPlanDropdowns(dropdowns?: string[]): Observable<PlanDropdownsResponse> {
    const params: { [key: string]: string } = {};
    if (dropdowns && dropdowns.length > 0) {
      params['dropdown'] = dropdowns.join(',');
    }

    return this.apiService
      .get<any>('plans/dropdowns', { params })
      .pipe(
        map((res: any) => (res && res.data ? (res.data as PlanDropdownsResponse) : (res as PlanDropdownsResponse))),
        catchError((err: HttpErrorResponse) => {
          const status = err?.status;
          const message =
            err?.error?.message || err?.message || err?.statusText || 'Failed to load plan dropdown options';
          return throwError(() => ({ status, message }));
        })
      );
  }

  /**
   * PUT /plans/{planId}
   * Update administrator plan details (tblPlan) for Add/Edit Plan screen.
   * Errors are normalized to { status, message } like PatientService.updatePlanExtraInfo.
   */
  updatePlan(planId: number | string, body: PlanUpdateRequest): Observable<PlanUpdateResponse> {
    return this.apiService.put<PlanUpdateResponse>(`plans/${planId}`, body).pipe(
      catchError((err: HttpErrorResponse) => {
        const status = err?.status;
        const message =
          err?.error?.message || err?.message || err?.statusText || 'Failed to update plan';
        return throwError(() => ({ status, message }));
      })
    );
  }
}

